import { useEffect, useState } from 'react'

const CACHE_KEY = 'gh-activity-v1'
const CACHE_TTL = 60 * 60 * 1000 // 1 hour keeps us well inside GitHub's 60 req/hour anonymous limit.

const readCache = () => {
  try {
    const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY))
    if (cached && Date.now() - cached.savedAt < CACHE_TTL) return cached.data
  } catch (error) { /* storage unavailable */ }
  return null
}

const writeCache = (data) => {
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ savedAt: Date.now(), data })) } catch (error) { /* ignore */ }
}

const getJson = async (url) => {
  const response = await fetch(url, { headers: { Accept: 'application/json' } })
  if (!response.ok) throw new Error(`${response.status} ${url}`)
  return response.json()
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// Turns the daily contribution calendar into streaks and weekday / month distributions.
export const summarise = (days) => {
  const weekday = WEEKDAYS.map((label) => ({ label, count: 0 }))
  const monthMap = new Map()
  let longest = 0
  let run = 0
  let best = { date: null, count: 0 }

  days.forEach(({ date, count }) => {
    const d = new Date(`${date}T00:00:00`)
    weekday[d.getDay()].count += count
    const key = date.slice(0, 7)
    monthMap.set(key, (monthMap.get(key) || 0) + count)
    run = count > 0 ? run + 1 : 0
    longest = Math.max(longest, run)
    if (count > best.count) best = { date, count }
  })

  // Current streak may end today or yesterday (today might not have commits yet).
  let current = 0
  let index = days.length - 1
  if (index >= 0 && days[index].count === 0) index -= 1
  while (index >= 0 && days[index].count > 0) { current += 1; index -= 1 }

  const months = [...monthMap.entries()].slice(-12).map(([key, count]) => ({
    key,
    label: new Date(`${key}-01T00:00:00`).toLocaleString('en', { month: 'short' }),
    year: key.slice(0, 4),
    count
  }))

  const total = days.reduce((sum, day) => sum + day.count, 0)
  const activeDays = days.filter((day) => day.count > 0).length
  const busiest = weekday.reduce((a, b) => (b.count > a.count ? b : a), weekday[0])

  return { total, activeDays, longest, current, best, weekday, months, busiest }
}

const useGitHubData = (username) => {
  const [state, setState] = useState(() => {
    const cached = readCache()
    return cached ? { status: 'ready', ...cached } : { status: 'loading' }
  })

  useEffect(() => {
    if (state.status === 'ready') return undefined
    let cancelled = false

    Promise.allSettled([
      getJson(`https://github-contributions-api.jogruber.de/v4/${username}?y=last`),
      getJson(`https://api.github.com/users/${username}/repos?per_page=100&sort=pushed`),
      getJson(`https://api.github.com/users/${username}`)
    ]).then(([calendar, repos, user]) => {
      if (cancelled) return
      if (calendar.status === 'rejected' && repos.status === 'rejected') {
        setState({ status: 'error' })
        return
      }

      const days = calendar.status === 'fulfilled' ? calendar.value.contributions || [] : []
      const ownRepos = repos.status === 'fulfilled' ? repos.value.filter((repo) => !repo.fork) : []

      const languageCounts = {}
      ownRepos.forEach((repo) => { if (repo.language) languageCounts[repo.language] = (languageCounts[repo.language] || 0) + 1 })
      const languages = Object.entries(languageCounts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 6)

      const recent = ownRepos
        .filter((repo) => repo.name.toLowerCase() !== username.toLowerCase())
        .slice(0, 6)
        .map((repo) => ({
          name: repo.name,
          url: repo.html_url,
          description: repo.description,
          language: repo.language,
          stars: repo.stargazers_count,
          pushedAt: repo.pushed_at
        }))

      const data = {
        days,
        summary: days.length ? summarise(days) : null,
        languages,
        recent,
        stars: ownRepos.reduce((sum, repo) => sum + repo.stargazers_count, 0),
        publicRepos: user.status === 'fulfilled' ? user.value.public_repos : ownRepos.length,
        ownRepoCount: ownRepos.length
      }
      writeCache(data)
      setState({ status: 'ready', ...data })
    })

    return () => { cancelled = true }
  }, [username, state.status])

  return state
}

export default useGitHubData
